import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CustomViewsAPI from '../../api/customViews';
import localeMessages from '../../i18n';

function unableToDeleteFilterMessage() {
  const locale = window.WOOT?.$i18n?.locale || 'en';
  const catalog = localeMessages[locale] || localeMessages.en;
  return (
    catalog?.FILTER?.CUSTOM_VIEWS?.DELETE?.UNABLE_TO_DELETE ||
    localeMessages.en.FILTER.CUSTOM_VIEWS.DELETE.UNABLE_TO_DELETE
  );
}

const FILTER_TYPE_ALIASES = {
  conversation: [0, 'conversation'],
  contact: [1, 'contact'],
  report: [2, 'report'],
  board: [3, 'board'],
};

export function customViewMatchesFilterType(recordType, filterType) {
  if (filterType == null || filterType === '') return false;
  const aliases = FILTER_TYPE_ALIASES[filterType];
  if (!aliases) return recordType === filterType;
  return aliases.includes(recordType);
}

export function mergeCustomViewsByFilterType(existingRecords, filterType, records) {
  const resolvedFilterType =
    filterType ||
    records?.[0]?.filter_type ||
    existingRecords?.[0]?.filter_type;
  if (!resolvedFilterType) return records || [];

  const remaining = (existingRecords || []).filter(
    record => !customViewMatchesFilterType(record.filter_type, resolvedFilterType)
  );
  return [...remaining, ...(records || [])];
}

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCustomViews(_state) {
    return _state.records;
  },
  getCustomViewsByFilterType: _state => filterType => {
    return _state.records.filter(record =>
      customViewMatchesFilterType(record.filter_type, filterType)
    );
  },
};

export const actions = {
  get: async function getCustomViews({ commit, state: customViewsState }, filterType) {
    if (filterType === 'board') {
      if (customViewsState.uiFlags.isFetching) {
        return customViewsState.records;
      }

      const hasCachedBoardFilters = customViewsState.records.some(record =>
        customViewMatchesFilterType(record.filter_type, 'board')
      );
      if (hasCachedBoardFilters) {
        return customViewsState.records;
      }
    }

    commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isFetching: true });
    try {
      const response = await CustomViewsAPI.getCustomViewsByFilterType(
        filterType
      );
      commit(types.SET_CUSTOM_VIEW, {
        filterType,
        records: response.data,
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createCustomViews({ commit }, obj) {
    commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isCreating: true });
    try {
      const response = await CustomViewsAPI.create(obj);
      commit(types.ADD_CUSTOM_VIEW, response.data);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isCreating: false });
    }
  },
  update: async function updateCustomViews({ commit }, obj) {
    commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isCreating: true });
    try {
      const response = await CustomViewsAPI.update(obj.id, obj);
      commit(types.UPDATE_CUSTOM_VIEW, response.data);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isCreating: false });
    }
  },
  delete: async ({ commit }, { id, filterType }) => {
    commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isDeleting: true });
    try {
      await CustomViewsAPI.deleteCustomViews(id, filterType);
      commit(types.DELETE_CUSTOM_VIEW, id);
    } catch (error) {
      const data = error?.response?.data;
      const message =
        data?.message ||
        (typeof data?.error === 'string' ? data.error : null);
      throw new Error(message || unableToDeleteFilterMessage());
    } finally {
      commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_CUSTOM_VIEW_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CUSTOM_VIEW]: MutationHelpers.create,
  [types.SET_CUSTOM_VIEW](state, payload) {
    if (Array.isArray(payload)) {
      state.records = payload;
      return;
    }

    const { filterType, records } = payload || {};
    state.records = mergeCustomViewsByFilterType(
      state.records,
      filterType,
      records
    );
  },
  [types.UPDATE_CUSTOM_VIEW]: MutationHelpers.update,
  [types.DELETE_CUSTOM_VIEW]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
